import axios from "axios";
import { authClient } from "lib/auth-client";
import {
  getBearerSessionToken,
  isJwtToken,
  resolveAuthBearerToken,
  setBearerSessionToken,
} from "lib/better-auth-session";
import { store } from "store/actions/store";
import { setRefreshToken, setToken } from "store/reducers/auth";
import type { AppState } from "store/reducers/Reducer";
import { ServerError } from "utils/genericUtilityFunctions";
import { assertAuthSessionUnchanged } from "lib/auth-session-ownership";
import {
  assertAuthAttemptCurrent,
  authAttemptFetchOptions,
  beginReauthenticationAttempt,
  bindAuthAttempt,
  type AuthAttempt,
} from "lib/auth-attempt";

type BetterAuthClientError = {
  status?: number;
  statusCode?: number;
  message?: string;
  code?: string;
};

type BetterAuthTwoFactorRedirect = {
  twoFactorRedirect?: boolean;
};

function resolveBetterAuthClientStatus(error: BetterAuthClientError, fallbackStatus = 400): number {
  if (typeof error.status === "number") {
    return error.status;
  }

  if (typeof error.statusCode === "number") {
    return error.statusCode;
  }

  if (
    error.code === "SESSION_NOT_FRESH" ||
    error.code === "SESSION_IS_NOT_FRESH" ||
    error.code === "UNAUTHORIZED" ||
    error.code === "TWO_FACTOR_REQUIRED"
  ) {
    return 403;
  }

  return fallbackStatus;
}

function toServerError(error: BetterAuthClientError, fallbackMessage: string): ServerError {
  return new ServerError(
    resolveBetterAuthClientStatus(error),
    error.message ?? fallbackMessage,
    undefined,
    error.code
  );
}

function isTwoFactorRedirect(data: unknown): data is BetterAuthTwoFactorRedirect {
  return (
    typeof data === "object" &&
    data !== null &&
    "twoFactorRedirect" in data &&
    (data as BetterAuthTwoFactorRedirect).twoFactorRedirect === true
  );
}

function getSessionTokenFromSignIn(data: unknown): string | null {
  if (
    typeof data !== "object" ||
    data === null ||
    !("token" in data) ||
    typeof data.token !== "string" ||
    !data.token.trim()
  ) {
    return null;
  }

  return data.token;
}

function extractSessionTokenFromGetSession(data: unknown): string | null {
  if (typeof data !== "object" || data === null || !("session" in data)) {
    return null;
  }

  const session = (data as { session?: { token?: string | null } | null }).session;
  const token = session?.token;
  return typeof token === "string" && token.trim() ? token : null;
}

export async function persistBetterAuthSessionToken(
  data: unknown,
  attempt: AuthAttempt = beginReauthenticationAttempt()
): Promise<void> {
  assertAuthAttemptCurrent(attempt);
  const owner = attempt.session;
  let sessionToken = getSessionTokenFromSignIn(data);

  if (!sessionToken) {
    const sessionResult = await authClient.getSession({
      fetchOptions: authAttemptFetchOptions(attempt),
    });
    assertAuthAttemptCurrent(attempt);
    sessionToken = extractSessionTokenFromGetSession(sessionResult.data);
  }

  if (!sessionToken) {
    const cachedToken = getBearerSessionToken();
    if (cachedToken && !isJwtToken(cachedToken)) {
      sessionToken = cachedToken;
    }
  }

  if (!sessionToken) {
    throw new ServerError(500, "Unable to refresh your session");
  }

  assertAuthAttemptCurrent(attempt);
  store.dispatch(setToken(sessionToken));
  assertAuthSessionUnchanged({ ...owner, token: sessionToken });
  store.dispatch(setRefreshToken(sessionToken));
  bindAuthAttempt(attempt, { ...owner, token: sessionToken, refreshToken: sessionToken });
  setBearerSessionToken(sessionToken);
}

async function ensureBetterAuthSessionToken(): Promise<string | null> {
  const state = store.getState() as AppState;
  const reduxToken = resolveAuthBearerToken(state.auth.token);
  if (reduxToken && !isJwtToken(reduxToken)) {
    return reduxToken;
  }

  const cached = getBearerSessionToken();
  if (cached && !isJwtToken(cached)) {
    return cached;
  }

  const sessionResult = await authClient.getSession();
  const sessionToken = sessionResult.data?.session?.token;
  if (typeof sessionToken === "string" && sessionToken.length > 0) {
    setBearerSessionToken(sessionToken);
    return sessionToken;
  }

  return null;
}

function isBetterAuthSessionNotFreshCode(code: string | undefined): boolean {
  return code === "SESSION_NOT_FRESH" || code === "SESSION_IS_NOT_FRESH";
}

function isBetterAuthSessionNotFreshMessage(message: string | undefined): boolean {
  return message?.trim().toLowerCase() === "session is not fresh";
}

export function isBetterAuthSessionNotFresh(error: unknown): boolean {
  if (error instanceof ServerError) {
    return (
      isBetterAuthSessionNotFreshCode(error.code) ||
      isBetterAuthSessionNotFreshMessage(error.message)
    );
  }

  if (error && typeof error === "object") {
    const record = error as Record<string, unknown>;

    if ("details" in record && isBetterAuthSessionNotFresh(record.details)) {
      return true;
    }

    if (typeof record.code === "string" && isBetterAuthSessionNotFreshCode(record.code)) {
      return true;
    }

    if (typeof record.message === "string" && isBetterAuthSessionNotFreshMessage(record.message)) {
      return true;
    }
  }

  return false;
}

export function normalizeStepUpRequestError(error: unknown): ServerError | null {
  if (error instanceof ServerError) {
    return error;
  }

  if (axios.isAxiosError(error)) {
    return ServerError.fromAxiosError(error);
  }

  return null;
}

export async function reauthenticateBetterAuthSession(
  email: string,
  password: string,
  attempt: AuthAttempt = beginReauthenticationAttempt()
): Promise<void> {
  const result = await authClient.signIn.email(
    { email: email.trim().toLowerCase(), password, rememberMe: true },
    authAttemptFetchOptions(attempt)
  );
  assertAuthAttemptCurrent(attempt);

  if (result.error) {
    throw toServerError(result.error, "Unable to verify your password");
  }

  if (isTwoFactorRedirect(result.data)) {
    throw new ServerError(
      403,
      "Complete two-factor verification to continue.",
      undefined,
      "TWO_FACTOR_REQUIRED"
    );
  }

  await persistBetterAuthSessionToken(result.data, attempt);
}

export async function completeBetterAuthMfaReauthentication(
  code: string,
  attempt: AuthAttempt = beginReauthenticationAttempt()
): Promise<void> {
  const result = await authClient.twoFactor.verifyTotp({ code }, authAttemptFetchOptions(attempt));
  assertAuthAttemptCurrent(attempt);
  if (result.error) {
    throw toServerError(result.error, "Unable to verify your authentication code");
  }

  await persistBetterAuthSessionToken(result.data, attempt);
}

export async function assertBetterAuthSession(): Promise<void> {
  const token = await ensureBetterAuthSessionToken();
  if (!token) {
    throw new ServerError(401, "Unauthorized");
  }
}

/** Matches Better Auth default `session.freshAge` (24 hours). */
export const BETTER_AUTH_DEFAULT_FRESH_AGE_MS = 24 * 60 * 60 * 1000;

function parseBetterAuthSessionCreatedAtMs(createdAt: unknown): number | null {
  if (createdAt instanceof Date) {
    const createdAtMs = createdAt.getTime();
    return Number.isNaN(createdAtMs) ? null : createdAtMs;
  }

  if (typeof createdAt === "string" || typeof createdAt === "number") {
    const createdAtMs = new Date(createdAt).getTime();
    return Number.isNaN(createdAtMs) ? null : createdAtMs;
  }

  return null;
}

export async function getBetterAuthSessionAgeMs(): Promise<number | null> {
  await assertBetterAuthSession();
  const sessionResult = await authClient.getSession();
  const createdAtMs = parseBetterAuthSessionCreatedAtMs(sessionResult.data?.session?.createdAt);
  if (createdAtMs === null) {
    return null;
  }

  return Date.now() - createdAtMs;
}

export async function isBetterAuthSessionFresh(
  freshAgeMs = BETTER_AUTH_DEFAULT_FRESH_AGE_MS
): Promise<boolean> {
  const ageMs = await getBetterAuthSessionAgeMs();
  if (ageMs === null) {
    return false;
  }

  return ageMs < freshAgeMs;
}

export async function assertBetterAuthSessionIsFresh(
  freshAgeMs = BETTER_AUTH_DEFAULT_FRESH_AGE_MS
): Promise<void> {
  if (!(await isBetterAuthSessionFresh(freshAgeMs))) {
    throw new ServerError(403, "Session is not fresh", undefined, "SESSION_NOT_FRESH");
  }
}
